import { useRef } from 'react';

import type { SeatData } from '@/components/grid';
import { stabilizeTimeKeys, type BoardSortMode, type RunwayAnchor } from './tile-layout';

function mapsEqual(a: ReadonlyMap<number, number>, b: ReadonlyMap<number, number>): boolean {
  if (a.size !== b.size) return false;
  for (const [k, v] of a) {
    if (b.get(k) !== v) return false;
  }
  return true;
}

/**
 * Memory for the `time` board order. Returns an anchor+predict committed-key
 * map (see {@link stabilizeTimeKeys}) so held seats stop swapping positions on
 * every repoll/SSE update — a tile only re-ranks when its runway genuinely
 * jumps (top-up / price change / new holder), never on sample wobble.
 *
 * Returns `undefined` in any non-time sort mode (the floor order is driven by
 * `selfAssessedPrice`, which is discrete and doesn't wobble). The returned map
 * keeps a stable identity across renders while its contents are unchanged —
 * which, with anchored keys, is every render between genuine changes — so
 * downstream `useMemo`s that depend on it don't recompute needlessly.
 */
export function useStableTimeOrder(
  seats: SeatData[],
  sortMode: BoardSortMode,
): ReadonlyMap<number, number> | undefined {
  const anchorsRef = useRef<Map<number, RunwayAnchor>>(new Map());
  const keysRef = useRef<Map<number, number>>(new Map());

  if (sortMode !== 'time') {
    if (anchorsRef.current.size > 0) anchorsRef.current = new Map();
    if (keysRef.current.size > 0) keysRef.current = new Map();
    return undefined;
  }

  const { keys, anchors } = stabilizeTimeKeys(seats, anchorsRef.current);
  anchorsRef.current = anchors;
  // Preserve referential identity when the keys are unchanged so memoised
  // consumers (board sort, placement diff) can skip recomputation.
  if (!mapsEqual(keys, keysRef.current)) keysRef.current = keys;
  return keysRef.current;
}
